import type { Chart } from "../Chart";

// Info passed to the interaction delegate (drawings) for each pointer event.
export interface PointerCtx {
  px: number; // plot-relative x (css px)
  py: number; // plot-relative y (css px)
  indexF: number; // fractional bar index under cursor
  index: number; // snapped integer bar index
  price: number;
  event: PointerEvent;
  region: Region;
}

export type Region = "plot" | "priceAxis" | "timeAxis" | "corner";

// Implemented by the drawing subsystem. Returning true = "I handled it";
// the controller then skips its default navigation behavior.
export interface InteractionDelegate {
  onDown(ctx: PointerCtx): boolean;
  onMove(ctx: PointerCtx): boolean; // called on hover too (button up)
  onUp(ctx: PointerCtx): boolean;
  onDblClick(ctx: PointerCtx): boolean;
  cursorFor(ctx: PointerCtx): string | null;
  isBusy(): boolean; // actively drawing/dragging a drawing
}

export class InputController {
  private chart: Chart;
  delegate: InteractionDelegate | null = null;
  onContextMenu: ((clientX: number, clientY: number, ctx: PointerCtx) => void) | null = null;

  private dragging = false;
  private dragMode: "pan" | "priceScale" | "timeScale" | null = null;
  private lastX = 0;
  private lastY = 0;
  private downX = 0;
  private downY = 0;
  private moved = false;

  // touch: active pointers (for pinch) + long-press → context menu
  private touches = new Map<number, { x: number; y: number }>();
  private pinch: { dist: number; midX: number; midY: number } | null = null;
  private pressTimer = 0;

  // momentum
  private velBars = 0;
  private lastMoveT = 0;
  private momId = 0;

  constructor(chart: Chart) {
    this.chart = chart;
    const el = chart.container;
    el.addEventListener("wheel", this.onWheel, { passive: false });
    el.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    el.addEventListener("dblclick", this.onDblClick);
    el.addEventListener("contextmenu", this.onContext);
    el.addEventListener("pointerleave", this.onLeave);
    window.addEventListener("pointercancel", this.onPointerCancel);
    el.style.touchAction = "none"; // the chart owns touch gestures (pan / pinch), not the page
  }

  private rectXY(e: PointerEvent | WheelEvent | MouseEvent): [number, number] {
    const r = this.chart.container.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }

  private regionOf(px: number, py: number): Region {
    const vp = this.chart.viewport;
    const inPlotX = px < vp.plot.width;
    const inPlotY = py < vp.plot.height;
    if (inPlotX && inPlotY) return "plot";
    if (!inPlotX && inPlotY) return "priceAxis";
    if (inPlotX && !inPlotY) return "timeAxis";
    return "corner";
  }

  private makeCtx(e: PointerEvent, px: number, py: number): PointerCtx {
    const vp = this.chart.viewport;
    const indexF = vp.indexOfX(px);
    const index = Math.floor(indexF);
    const price = vp.priceOfY(py);
    return { px, py, indexF, index, price, event: e, region: this.regionOf(px, py) };
  }

  private onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const [px, py] = this.rectXY(e);
    const region = this.regionOf(px, py);
    const chart = this.chart;

    if (region === "priceAxis") {
      chart.scalePriceByDrag(e.deltaY, py);
      return;
    }
    // horizontal scroll with shift or trackpad deltaX
    if (e.shiftKey && Math.abs(e.deltaX) < 1) {
      chart.panByPixels(-e.deltaY);
      return;
    }
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && !e.ctrlKey) {
      chart.panByPixels(-e.deltaX);
      return;
    }
    // zoom horizontally, anchored at cursor
    const factor = Math.exp(-e.deltaY * 0.0016);
    chart.zoomBy(factor, Math.min(px, chart.viewport.plot.width));
  };

  private onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 && e.button !== 1) return;
    const [px, py] = this.rectXY(e);
    const ctx = this.makeCtx(e, px, py);
    if (e.pointerType === "touch") {
      this.touches.set(e.pointerId, { x: px, y: py });
      if (this.touches.size === 2) {
        // second finger: switch from pan to pinch
        this.cancelPress();
        this.stopMomentum();
        this.dragging = false;
        this.dragMode = null;
        this.pinch = this.pinchState();
        return;
      }
      if (this.touches.size > 2) return;
      // finger down = show the crosshair/readout at that candle; hold to open the menu
      this.chart.updateCursor(px, py, true);
      this.cancelPress();
      this.pressTimer = window.setTimeout(() => {
        this.pressTimer = 0;
        if (this.moved || !this.onContextMenu) return;
        this.dragging = false;
        this.dragMode = null;
        this.onContextMenu(e.clientX, e.clientY, ctx);
      }, 550);
    }
    this.downX = px;
    this.downY = py;
    this.moved = false;
    this.stopMomentum();

    // let drawings intercept first (in plot)
    if (this.delegate && ctx.region === "plot" && e.button === 0) {
      if (this.delegate.onDown(ctx)) {
        return;
      }
    }

    this.dragging = true;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    this.lastX = px;
    this.lastY = py;
    this.velBars = 0;
    this.lastMoveT = performance.now();
    if (ctx.region === "priceAxis") this.dragMode = "priceScale";
    else if (ctx.region === "timeAxis") this.dragMode = "timeScale";
    else this.dragMode = "pan";
    this.chart.container.style.cursor = this.dragMode === "pan" ? "grabbing" : this.dragMode === "priceScale" ? "ns-resize" : "ew-resize";
  };

  private onPointerMove = (e: PointerEvent) => {
    const [px, py] = this.rectXY(e);
    if (e.pointerType === "touch" && this.touches.has(e.pointerId)) {
      this.touches.set(e.pointerId, { x: px, y: py });
      if (this.pinch && this.touches.size >= 2) {
        const now = this.pinchState();
        if (now.dist > 10 && this.pinch.dist > 10) this.chart.zoomBy(now.dist / this.pinch.dist, Math.min(now.midX, this.chart.viewport.plot.width));
        this.chart.panByPixels(now.midX - this.pinch.midX);
        if (now.midY !== this.pinch.midY) this.chart.shiftPrice(now.midY - this.pinch.midY);
        this.pinch = now;
        return;
      }
      if (Math.abs(px - this.downX) > 8 || Math.abs(py - this.downY) > 8) this.cancelPress();
    }

    if (!this.dragging) {
      // hover
      const overEl = this.isOverChart(e);
      this.chart.updateCursor(px, py, overEl);
      if (this.delegate && overEl) {
        const ctx = this.makeCtx(e, px, py);
        this.delegate.onMove(ctx);
        const cur = this.delegate.cursorFor(ctx);
        if (cur !== null) this.chart.container.style.cursor = cur;
        else this.chart.container.style.cursor = this.regionOf(px, py) === "plot" ? "crosshair" : "default";
      }
      return;
    }

    const dx = px - this.lastX;
    const dy = py - this.lastY;
    if (Math.abs(px - this.downX) > 3 || Math.abs(py - this.downY) > 3) this.moved = true;

    // drawing drag in progress
    if (this.delegate && this.delegate.isBusy()) {
      const ctx = this.makeCtx(e, px, py);
      this.delegate.onMove(ctx);
      this.lastX = px;
      this.lastY = py;
      return;
    }

    if (this.dragMode === "pan") {
      this.chart.panByPixels(dx);
      // free vertical panning: drag up/down anywhere to move the price view
      if (dy !== 0) this.chart.shiftPrice(dy);
      const now = performance.now();
      const dt = Math.max(1, now - this.lastMoveT);
      this.velBars = (-dx / this.chart.viewport.barSpacing) / (dt / 1000);
      this.lastMoveT = now;
    } else if (this.dragMode === "priceScale") {
      this.chart.scalePriceByDrag(dy, this.downY);
    } else if (this.dragMode === "timeScale") {
      const factor = Math.exp(dx * 0.005);
      this.chart.zoomBy(factor, this.chart.viewport.plot.width * 0.5);
    }
    this.chart.updateCursor(px, py, true);
    this.lastX = px;
    this.lastY = py;
  };

  private onPointerUp = (e: PointerEvent) => {
    if (e.pointerType === "touch") {
      this.cancelPress();
      this.touches.delete(e.pointerId);
      if (this.pinch) {
        if (this.touches.size < 2) this.pinch = null;
        return; // lifting one finger of a pinch must not fling the chart
      }
    }
    if (this.delegate && this.delegate.isBusy()) {
      const [px, py] = this.rectXY(e);
      this.delegate.onUp(this.makeCtx(e, px, py));
    }
    if (this.dragging && this.dragMode === "pan" && this.moved) {
      this.startMomentum();
    }
    this.dragging = false;
    this.dragMode = null;
    this.chart.container.style.cursor = "crosshair";
  };

  private onPointerCancel = (e: PointerEvent) => {
    this.cancelPress();
    this.touches.delete(e.pointerId);
    if (this.touches.size < 2) this.pinch = null;
    this.dragging = false;
    this.dragMode = null;
  };

  private pinchState() {
    const [a, b] = [...this.touches.values()];
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), midX: (a.x + b.x) / 2, midY: (a.y + b.y) / 2 };
  }

  private cancelPress() {
    if (this.pressTimer) clearTimeout(this.pressTimer);
    this.pressTimer = 0;
  }

  private onDblClick = (e: MouseEvent) => {
    const [px, py] = this.rectXY(e);
    const region = this.regionOf(px, py);
    if (region === "priceAxis") {
      this.chart.resetPriceScale();
      return;
    }
    if (this.delegate && region === "plot") {
      const ctx = this.makeCtx(e as unknown as PointerEvent, px, py);
      if (this.delegate.onDblClick(ctx)) return;
    }
  };

  private onContext = (e: MouseEvent) => {
    e.preventDefault();
    const [px, py] = this.rectXY(e);
    const ctx = this.makeCtx(e as unknown as PointerEvent, px, py);
    if (this.onContextMenu) this.onContextMenu(e.clientX, e.clientY, ctx);
  };

  private onLeave = (e: PointerEvent) => {
    if (e.pointerType === "touch") return; // keep the last tapped candle's readout on screen
    this.chart.updateCursor(0, 0, false);
  };

  private isOverChart(e: PointerEvent): boolean {
    const r = this.chart.container.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  }

  // ---- momentum ----
  private startMomentum() {
    const friction = 0.92;
    const step = () => {
      this.velBars *= friction;
      if (Math.abs(this.velBars) < 0.02) {
        this.stopMomentum();
        return;
      }
      this.chart.panByBars(this.velBars / 60);
      this.momId = requestAnimationFrame(step);
    };
    this.stopMomentum();
    this.momId = requestAnimationFrame(step);
  }
  private stopMomentum() {
    if (this.momId) cancelAnimationFrame(this.momId);
    this.momId = 0;
  }
}
